/**
 * Desktop beta update nudge (#111).
 *
 * Rust returns a candidate when a newer git tag exists than the running app.
 * This module decides whether to show the splash dialog (dismiss + safety).
 */

import { open } from "@tauri-apps/plugin-dialog";
import { appVersion } from "./appVersion";
import { openDialogDefaults } from "./dialogDefaults";
import { resolveHomeDir } from "./tauri/dialogHome";
import { tauriOnlyInvoke } from "./invoke";
import {
  isJust,
  isNothing,
  just,
  nothing,
  type Maybe,
  matchMaybe,
  toMaybe,
} from "./maybe";
import {
  readStringPref,
  writeStringPref,
  type LocalPrefName,
} from "./localPrefs";
import { err, ok, type Result } from "./result";
import { isTauri } from "./runtime";
import { isMobileShell } from "./tauri/core";

export type BetaUpdateInfo = {
  current: string;
  latest: string;
  sourceRoot: string;
};

const DISMISSED_PREF = "betaUpdateDismissed" as const satisfies LocalPrefName;

/**
 * Return true when splash should show the Update / Later dialog.
 *
 * - `latest` must be strictly newer than `current` (semver; ignore a leading `v`).
 * - If the user previously chose Later for `dismissedVersion`, only ask again
 *   when `latest` is strictly newer than that dismissed version.
 * - Malformed versions do not prompt.
 */
export function shouldOfferBetaUpdate(
  current: string,
  latest: string,
  dismissedVersion: Maybe<string>,
): boolean {
  const currentParts = parseSemver(current);
  const latestParts = parseSemver(latest);
  if (isNothing(currentParts) || isNothing(latestParts)) return false;
  if (compareSemver(latestParts.value, currentParts.value) <= 0) return false;
  return matchMaybe(dismissedVersion, {
    nothing: () => true,
    just: (dismissed) => {
      if (dismissed.length === 0) return true;
      const dismissedParts = parseSemver(dismissed);
      if (isNothing(dismissedParts)) return false;
      return compareSemver(latestParts.value, dismissedParts.value) > 0;
    },
  });
}

type Semver = [number, number, number];

function parseSemver(raw: string): Maybe<Semver> {
  const s = raw.trim().replace(/^v/i, "");
  const m = /^(\d+)\.(\d+)\.(\d+)(?:[.+-].*)?$/.exec(s);
  if (m === null) return nothing;
  return just([Number(m[1]), Number(m[2]), Number(m[3])]);
}

function compareSemver(a: Semver, b: Semver): number {
  if (a[0] !== b[0]) return a[0] < b[0] ? -1 : 1;
  if (a[1] !== b[1]) return a[1] < b[1] ? -1 : 1;
  if (a[2] !== b[2]) return a[2] < b[2] ? -1 : 1;
  return 0;
}

export function readDismissedBetaVersion(): Maybe<string> {
  return readStringPref(DISMISSED_PREF);
}

export function dismissBetaVersion(version: string): void {
  writeStringPref(DISMISSED_PREF, version.trim());
}

function normalizeBetaUpdateInfo(raw: unknown): Maybe<BetaUpdateInfo> {
  if (raw === null || typeof raw !== "object") return nothing;
  const obj = raw as Record<string, unknown>;
  const current = typeof obj.current === "string" ? obj.current : "";
  const latest = typeof obj.latest === "string" ? obj.latest : "";
  const sourceRoot = typeof obj.sourceRoot === "string" ? obj.sourceRoot : "";
  if (current.length === 0 || latest.length === 0 || sourceRoot.length === 0) {
    return nothing;
  }
  return just({ current, latest, sourceRoot });
}

export async function fetchBetaUpdateInfo(): Promise<Maybe<BetaUpdateInfo>> {
  if (isTauri() === false) return nothing;
  if (await isMobileShell()) return nothing;
  try {
    const raw = await tauriOnlyInvoke<unknown>("check_beta_update");
    return normalizeBetaUpdateInfo(raw);
  } catch (error) {
    console.warn("Beta update check failed", error);
    return nothing;
  }
}

/**
 * Map the published-tag check into an offer. Nothing means this build is
 * already on the newest tag. A manual check ignores the splash Later choice.
 */
export function offerFromPublishedTag(
  current: string,
  latest: Maybe<string>,
): Maybe<string> {
  return matchMaybe(latest, {
    nothing: () => nothing,
    just: (tag) =>
      tag.length === 0 || shouldOfferBetaUpdate(current, tag, nothing) === false
        ? nothing
        : just(tag),
  });
}

/**
 * About "Check for updates" (#351). Fetches the `v*` tags `release-beta.sh`
 * publishes, then compares them to this build. Ok(Just(latest)) when a newer
 * tag exists, Ok(Nothing) when none, Err(message) when the fetch failed.
 * Unlike the splash probe, this does not treat a missing checkout as
 * "up to date".
 */
export async function checkBetaUpdateNow(): Promise<
  Result<Maybe<string>, string>
> {
  try {
    const raw = await tauriOnlyInvoke<unknown>("check_published_beta_update");
    if (raw === null) return ok(nothing);
    if (typeof raw === "string") {
      return ok(offerFromPublishedTag(appVersion(), just(raw)));
    }
    return err("Update check returned an unexpected result.");
  } catch (error) {
    return err(error instanceof Error ? error.message : String(error));
  }
}

/**
 * The checkout `get_beta_source_root` already knows about (baked path or a
 * saved folder), if that directory exists. Does not prompt.
 */
export async function readBetaSourceRoot(): Promise<Maybe<string>> {
  if (isTauri() === false) return nothing;
  try {
    const raw = await tauriOnlyInvoke<unknown>("get_beta_source_root");
    const text = typeof raw === "string" ? raw.trim() : "";
    return text.length === 0 ? nothing : just(text);
  } catch (error) {
    console.warn("Beta source root unavailable", error);
    return nothing;
  }
}

/** Ensure a source checkout path exists; prompt once if bake-in is missing. */
export async function ensureBetaSourceRoot(): Promise<Maybe<string>> {
  if (isTauri() === false) return nothing;
  try {
    const existingRaw = await tauriOnlyInvoke<unknown>("get_beta_source_root");
    const existingM = toMaybe(
      typeof existingRaw === "string" ? existingRaw : "",
    );
    if (isJust(existingM) && existingM.value.length > 0) return existingM;
    const home = await resolveHomeDir();
    const picked = await open({
      ...openDialogDefaults(home),
      directory: true,
      multiple: false,
      title: "Select your zw-app checkout",
    });
    if (typeof picked !== "string" || picked.length === 0) return nothing;
    await tauriOnlyInvoke("set_beta_source_root", { path: picked });
    return just(picked);
  } catch (error) {
    console.warn("Beta source root unavailable", error);
    return nothing;
  }
}

export async function startBetaUpdate(): Promise<void> {
  await tauriOnlyInvoke("start_beta_update");
}

/**
 * Start as early as splash mounts (in parallel with recents). Returns info to
 * show, or Nothing to skip. Callers must not await this on the boot path.
 */
export async function probeBetaUpdateOffer(): Promise<Maybe<BetaUpdateInfo>> {
  const infoM = await fetchBetaUpdateInfo();
  return matchMaybe(infoM, {
    nothing: () => nothing,
    just: (info) => {
      if (
        shouldOfferBetaUpdate(
          info.current,
          info.latest,
          readDismissedBetaVersion(),
        ) === false
      ) {
        return nothing;
      }
      return just(info);
    },
  });
}
